/**
 * Modal to edit an existing license.
 *
 * Extracted from LicenseManagementPage.tsx (Sub-PR 6.3).
 */

import { useState } from 'react'
import type { License, LicenseType, UpdateLicenseRequest } from '../../types'
import { type LicenseFormData, toDateInputValue } from './helpers'

const DEFAULT_SEATS_TOTAL = 1

interface EditLicenseModalProps {
  license: License
  onClose: () => void
  onSave: (data: UpdateLicenseRequest) => Promise<void>
}

interface ModalHeaderProps {
  onClose: () => void
}

function ModalHeader({ onClose }: ModalHeaderProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4 dark:border-stone-700">
      <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">Edit License</h2>
      <button
        onClick={onClose}
        className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}

interface FormBodyProps {
  license: License
  formData: LicenseFormData
  setFormData: (data: LicenseFormData) => void
  saving: boolean
  error: string | null
  onClose: () => void
}

function LicenseKeyDisplay({ licenseKey }: { licenseKey: string }): React.ReactElement {
  return (
    <div className="rounded-lg bg-stone-50 p-3 dark:bg-stone-700/50">
      <p className="text-sm text-stone-600 dark:text-stone-400">
        <span className="font-medium text-stone-900 dark:text-stone-100">License Key:</span>{' '}
        <code className="rounded bg-stone-200 px-1.5 py-0.5 text-xs dark:bg-stone-600">
          {licenseKey}
        </code>
      </p>
    </div>
  )
}

function FormFields({
  formData,
  setFormData,
}: {
  formData: LicenseFormData
  setFormData: (data: LicenseFormData) => void
}): React.ReactElement {
  return (
    <>
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Total Seats *
        </label>
        <input
          type="number"
          min="1"
          value={formData.seatsTotal}
          onChange={(event) => {
            const parsed = Number.parseInt(event.target.value, 10)
            const seatsTotal = Number.isNaN(parsed) ? DEFAULT_SEATS_TOTAL : parsed
            setFormData({ ...formData, seatsTotal })
          }}
          className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
          required
        />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Features
        </label>
        <input
          type="text"
          value={formData.features}
          onChange={(event) => {
            setFormData({ ...formData, features: event.target.value })
          }}
          placeholder="feature1, feature2, feature3"
          className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 placeholder-stone-400 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
        />
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Comma-separated list of feature flags
        </p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-stone-700 dark:text-stone-300">
          Valid Until
        </label>
        <input
          type="date"
          value={formData.validUntil}
          onChange={(event) => {
            setFormData({ ...formData, validUntil: event.target.value })
          }}
          className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
        />
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          Leave empty for no expiration
        </p>
      </div>
    </>
  )
}

function FormActions({
  saving,
  onClose,
}: {
  saving: boolean
  onClose: () => void
}): React.ReactElement {
  return (
    <div className="flex justify-end gap-3 pt-4">
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
      >
        Cancel
      </button>
      <button
        type="submit"
        disabled={saving}
        className="rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </button>
    </div>
  )
}

interface FormProps extends FormBodyProps {
  onSubmit: (event: React.FormEvent) => void
}

function Form(props: FormProps): React.ReactElement {
  const { license, formData, setFormData, saving, error, onClose, onSubmit } = props
  return (
    <form onSubmit={onSubmit} className="space-y-4 p-6">
      {error !== null && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}
      <LicenseKeyDisplay licenseKey={license.licenseKey} />
      <FormFields formData={formData} setFormData={setFormData} />
      <FormActions saving={saving} onClose={onClose} />
    </form>
  )
}

function buildUpdateData(formData: LicenseFormData): UpdateLicenseRequest {
  const features = formData.features
    .split(',')
    .map((feature) => feature.trim())
    .filter(Boolean)
  const updateData: UpdateLicenseRequest = { seatsTotal: formData.seatsTotal }
  if (features.length > 0) updateData.features = features
  if (formData.validUntil) updateData.validUntil = formData.validUntil
  return updateData
}

export function EditLicenseModal({
  license,
  onClose,
  onSave,
}: EditLicenseModalProps): React.ReactElement {
  const [formData, setFormData] = useState<LicenseFormData>({
    organisationId: license.organisationId,
    licenseKey: license.licenseKey,
    licenseType: license.licenseType as LicenseType,
    seatsTotal: license.seatsTotal,
    features: license.features.join(', '),
    validFrom: toDateInputValue(license.validFrom),
    validUntil: toDateInputValue(license.validUntil),
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setError(null)
    setSaving(true)
    try {
      await onSave(buildUpdateData(formData))
      onClose()
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Failed to save license')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <ModalHeader onClose={onClose} />
        <Form
          license={license}
          formData={formData}
          setFormData={setFormData}
          saving={saving}
          error={error}
          onClose={onClose}
          onSubmit={(event) => {
            void handleSubmit(event)
          }}
        />
      </div>
    </div>
  )
}
